import { createContext, useContext, type ReactNode } from "react";

import {
  PLAN_FEATURES_UNRESTRICTED,
  isIntegrationAllowed as isIntegrationAllowedPure,
  isRegionAllowed as isRegionAllowedPure,
  type AllowedIntegrations,
  type AllowedRegions,
  type PlanFeatures,
} from "../branding/planFeaturesBranding";
import type { IntegrationKind } from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import type { Region } from "@chirpstack/chirpstack-api-grpc-web/common/common_pb";
import SessionStore from "../stores/SessionStore";

// Tenant-scoped context for ioX-Bridge plan-feature gates. Set by
// TenantLoader once the tenant is fetched; everything mounted under
// /tenants/:id/* reads through usePlanFeatures().
//
// Outside a tenant (global dashboard, admin-only pages) the default is
// PLAN_FEATURES_UNRESTRICTED — the gates shouldn't fire on screens that
// aren't tenant-owned.
const PlanFeaturesContext = createContext<PlanFeatures>(PLAN_FEATURES_UNRESTRICTED);

export function PlanFeaturesProvider(props: { features: PlanFeatures; children: ReactNode }) {
  return (
    <PlanFeaturesContext.Provider value={props.features}>{props.children}</PlanFeaturesContext.Provider>
  );
}

export function usePlanFeatures(): PlanFeatures {
  return useContext(PlanFeaturesContext);
}

/**
 * Gate hook tuned for the common "show/hide based on plan" case. Global
 * server admins (SessionStore.isAdmin) ALWAYS see the unrestricted UI,
 * per product decision — they manage all tenants and need to see every
 * feature to do that. Tenant admins + tenant users get the real gated
 * view.
 *
 * Returning a plain object rather than the raw features lets callers
 * write `const { canAddApplication } = usePlanGates(...)` without
 * sprinkling isAdmin checks through every view.
 */
export function usePlanGates() {
  const features = usePlanFeatures();
  const globalAdmin = SessionStore.isAdmin();

  const atApplicationsLimit = (current: number) => {
    if (globalAdmin || !features.hasPlanTags) return false;
    if (features.maxApplications === 0) return false; // 0 = unlimited
    return current >= features.maxApplications;
  };

  const isIntegrationAllowed = (kind: IntegrationKind) => {
    if (globalAdmin || !features.hasPlanTags) return true;
    return isIntegrationAllowedPure(features.allowedIntegrations, kind);
  };

  const canUseServerDeviceProfiles = () => {
    if (globalAdmin || !features.hasPlanTags) return true;
    return features.allowServerDeviceProfiles;
  };

  const isRegionAllowed = (region: Region) => {
    if (globalAdmin || !features.hasPlanTags) return true;
    return isRegionAllowedPure(features.allowedRegions, region);
  };

  return {
    features,
    globalAdmin,
    atApplicationsLimit,
    isIntegrationAllowed,
    canUseServerDeviceProfiles,
    isRegionAllowed,
  };
}

// Re-export for convenience so callers don't need two imports.
export type { AllowedIntegrations, AllowedRegions, PlanFeatures };
